<template>
    <section
        aria-label="Agente de risco"
        class="relative flex min-h-0 flex-col bg-white dark:bg-gray-900"
    >
        <!-- Header -->
        <header
            class="flex h-12 shrink-0 items-center justify-between gap-3 border-b border-gray-100 px-4 dark:border-white/5"
        >
            <div class="flex min-w-0 items-center gap-2.5">
                <span
                    class="flex h-7 w-7 shrink-0 items-center justify-center rounded-lg bg-gray-900 text-white dark:bg-white dark:text-gray-900"
                >
                    <SparklesIcon class="size-3.5" aria-hidden="true" />
                </span>
                <span
                    class="truncate text-sm font-semibold text-gray-900 dark:text-white"
                >
                    Agente de risco
                </span>
                <span
                    class="inline-flex shrink-0 items-center gap-1.5 text-xs text-gray-400 dark:text-gray-500"
                >
                    <span class="relative flex h-1.5 w-1.5">
                        <span
                            v-if="streaming"
                            class="absolute inline-flex h-full w-full animate-ping rounded-full bg-indigo-400 opacity-75"
                        ></span>
                        <span
                            class="relative inline-flex h-1.5 w-1.5 rounded-full"
                            :class="
                                streaming ? 'bg-indigo-500' : 'bg-emerald-500'
                            "
                        ></span>
                    </span>
                    {{ streaming ? "Investigando" : "Pronto" }}
                </span>
            </div>

            <div class="flex shrink-0 items-center gap-1">
                <span
                    v-if="verdict"
                    class="mr-1 inline-flex items-center gap-1 rounded-full px-2 py-0.5 text-[11px] font-semibold"
                    :class="verdictBadgeClass"
                    v-tooltip="verdictTooltip"
                >
                    {{ verdictLabel }}
                    <span
                        v-if="confidence !== null"
                        class="font-medium opacity-70"
                    >
                        {{ confidence }}%
                    </span>
                </span>
                <button
                    type="button"
                    class="flex h-8 w-8 items-center justify-center rounded-lg text-gray-400 transition-colors hover:bg-gray-100 hover:text-gray-700 focus:outline-none focus-visible:ring-2 focus-visible:ring-indigo-500 disabled:pointer-events-none disabled:opacity-40 dark:text-gray-500 dark:hover:bg-white/5 dark:hover:text-gray-200"
                    v-tooltip="'Nova conversa'"
                    aria-label="Nova conversa"
                    :disabled="streaming || messages.length === 0"
                    @click="clearConversation"
                >
                    <SquarePenIcon class="size-4" aria-hidden="true" />
                </button>
                <button
                    type="button"
                    class="flex h-8 w-8 items-center justify-center rounded-lg text-gray-400 transition-colors hover:bg-gray-100 hover:text-gray-700 focus:outline-none focus-visible:ring-2 focus-visible:ring-indigo-500 dark:text-gray-500 dark:hover:bg-white/5 dark:hover:text-gray-200"
                    v-tooltip="'Tour da tela'"
                    aria-label="Tour da tela"
                    @click="$emit('tour')"
                >
                    <CircleHelpIcon class="size-4" aria-hidden="true" />
                </button>
            </div>
        </header>

        <!-- Conversa -->
        <Conversation
            v-if="enabled && (thread.length > 0 || streaming)"
            class="min-h-0 flex-1"
        >
            <ConversationContent
                class="mx-auto w-full max-w-2xl gap-7 px-5 py-6"
            >
                <template
                    v-for="(message, index) in thread"
                    :key="`m-${index}`"
                >
                    <!-- Analista -->
                    <div
                        v-if="message.role === 'user'"
                        class="flex justify-end"
                    >
                        <div
                            class="max-w-[85%] whitespace-pre-wrap rounded-3xl bg-gray-100 px-4 py-2.5 text-sm leading-6 text-gray-900 dark:bg-white/10 dark:text-gray-100"
                        >
                            {{ message.text }}
                        </div>
                    </div>

                    <!-- Agente -->
                    <article v-else class="group/msg flex flex-col gap-3">
                        <ChainOfThought
                            v-if="message.steps.length > 0"
                            v-model="message.stepsOpen"
                        >
                            <ChainOfThoughtHeader
                                class="text-xs text-gray-400 dark:text-gray-500"
                            >
                                Consultou {{ message.steps.length }}
                                {{
                                    message.steps.length === 1
                                        ? "fonte"
                                        : "fontes"
                                }}
                            </ChainOfThoughtHeader>
                            <ChainOfThoughtContent>
                                <ChainOfThoughtStep
                                    v-for="step in message.steps"
                                    :key="step.id"
                                    :label="step.label"
                                    :description="step.detail"
                                    status="complete"
                                >
                                    <template #icon>
                                        <CheckCircle2Icon
                                            class="size-4 text-emerald-500"
                                            aria-hidden="true"
                                        />
                                    </template>
                                </ChainOfThoughtStep>
                            </ChainOfThoughtContent>
                        </ChainOfThought>

                        <message-blocks :blocks="message.blocks" />

                        <!-- Evidência no próprio chat: os números da visão
                             que sustenta a resposta, com atalho pro painel -->
                        <div
                            v-if="navFor(index)"
                            class="overflow-hidden rounded-xl border bg-white transition-colors dark:bg-white/[0.02]"
                            :class="
                                activeView === viewOf(navFor(index).target)
                                    ? 'border-indigo-200 dark:border-indigo-400/30'
                                    : 'border-gray-200 dark:border-white/10'
                            "
                        >
                            <div
                                v-if="evidenceFor(navFor(index).target).length"
                                class="grid grid-cols-2 gap-px border-b border-gray-200 bg-gray-200 dark:border-white/10 dark:bg-white/10"
                            >
                                <div
                                    v-for="(card, cardIndex) in evidenceFor(
                                        navFor(index).target,
                                    )"
                                    :key="card.key"
                                    class="flex min-w-0 flex-col bg-white dark:bg-gray-900"
                                    :class="
                                        cardIndex ===
                                            evidenceFor(navFor(index).target)
                                                .length -
                                                1 && cardIndex % 2 === 0
                                            ? 'col-span-2'
                                            : ''
                                    "
                                >
                                    <div class="px-3 pb-1.5 pt-2.5">
                                        <div class="flex items-center gap-1.5">
                                            <span
                                                class="h-1.5 w-1.5 shrink-0 rounded-full"
                                                :class="
                                                    evidenceDot(card.status)
                                                "
                                            ></span>
                                            <span
                                                class="truncate text-[10px] font-medium uppercase tracking-[0.12em] text-gray-500 dark:text-gray-400"
                                            >
                                                {{ card.label }}
                                            </span>
                                        </div>
                                        <p
                                            class="mt-1 truncate font-mono text-[15px] font-semibold tabular-nums tracking-tight text-gray-900 dark:text-white"
                                        >
                                            {{ card.value }}
                                        </p>
                                        <p
                                            class="truncate text-[11px] text-gray-500 dark:text-gray-400"
                                            :title="card.secondary"
                                        >
                                            {{ card.secondary }}
                                        </p>
                                    </div>
                                    <svg
                                        v-if="
                                            card.series &&
                                            card.series.length > 1
                                        "
                                        class="mt-auto block h-6 w-full"
                                        :class="
                                            card.status === 'critical'
                                                ? 'text-rose-500'
                                                : 'text-indigo-500'
                                        "
                                        viewBox="0 0 100 24"
                                        preserveAspectRatio="none"
                                        aria-hidden="true"
                                    >
                                        <path
                                            :d="sparkPath(card.series, true)"
                                            class="fill-current opacity-10"
                                        />
                                        <path
                                            :d="sparkPath(card.series)"
                                            fill="none"
                                            class="stroke-current"
                                            stroke-width="1.3"
                                            vector-effect="non-scaling-stroke"
                                        />
                                    </svg>
                                </div>
                            </div>

                            <button
                                type="button"
                                class="group/card flex w-full items-center gap-2.5 px-3 py-2 text-left transition-colors hover:bg-gray-50 focus:outline-none focus-visible:ring-2 focus-visible:ring-inset focus-visible:ring-indigo-500 dark:hover:bg-white/5"
                                @click="$emit('navigate', navFor(index).target)"
                            >
                                <component
                                    :is="viewIcon(navFor(index).target)"
                                    class="size-3.5 shrink-0 text-gray-500 dark:text-gray-400"
                                    aria-hidden="true"
                                />
                                <span
                                    class="min-w-0 flex-1 truncate text-xs font-medium text-gray-700 dark:text-gray-200"
                                >
                                    {{ viewLabel(navFor(index).target) }}
                                </span>
                                <span
                                    class="shrink-0 text-xs text-gray-500 dark:text-gray-400"
                                >
                                    {{
                                        activeView ===
                                        viewOf(navFor(index).target)
                                            ? "Aberto no painel"
                                            : "Abrir no painel"
                                    }}
                                </span>
                                <ArrowUpRightIcon
                                    class="size-3.5 shrink-0 text-gray-400 transition-transform group-hover/card:-translate-y-0.5 group-hover/card:translate-x-0.5"
                                    aria-hidden="true"
                                />
                            </button>
                        </div>

                        <div
                            class="flex items-center gap-1 opacity-0 transition-opacity group-hover/msg:opacity-100 focus-within:opacity-100"
                        >
                            <button
                                type="button"
                                class="flex h-7 w-7 items-center justify-center rounded-md text-gray-400 transition-colors hover:bg-gray-100 hover:text-gray-700 focus:outline-none dark:hover:bg-white/5 dark:hover:text-gray-200"
                                :aria-label="
                                    copiedIndex === index
                                        ? 'Copiado'
                                        : 'Copiar resposta'
                                "
                                v-tooltip="
                                    copiedIndex === index ? 'Copiado' : 'Copiar'
                                "
                                @click="copy(message.text, index)"
                            >
                                <CheckIcon
                                    v-if="copiedIndex === index"
                                    class="size-3.5"
                                    aria-hidden="true"
                                />
                                <CopyIcon
                                    v-else
                                    class="size-3.5"
                                    aria-hidden="true"
                                />
                            </button>
                        </div>
                    </article>
                </template>

                <!-- Turno em andamento -->
                <article v-if="streaming" class="flex flex-col gap-3">
                    <div
                        v-if="liveSteps.length === 0 && !liveText"
                        class="flex items-center gap-2 py-0.5"
                    >
                        <Loader :size="14" class="text-indigo-400" />
                        <Shimmer as="span" class="text-sm"
                            >Lendo a pergunta...</Shimmer
                        >
                    </div>

                    <ChainOfThought
                        v-if="liveSteps.length > 0"
                        :model-value="true"
                    >
                        <ChainOfThoughtHeader class="text-xs">
                            <Shimmer as="span" class="text-xs"
                                >Investigando</Shimmer
                            >
                        </ChainOfThoughtHeader>
                        <ChainOfThoughtContent>
                            <ChainOfThoughtStep
                                v-for="step in liveSteps"
                                :key="step.id"
                                :label="step.label"
                                :description="step.detail"
                                :status="step.done ? 'complete' : 'active'"
                            >
                                <template #icon>
                                    <CheckCircle2Icon
                                        v-if="step.done"
                                        class="size-4 text-emerald-500"
                                        aria-hidden="true"
                                    />
                                    <Loader
                                        v-else
                                        :size="14"
                                        class="text-indigo-400"
                                    />
                                </template>
                            </ChainOfThoughtStep>
                        </ChainOfThoughtContent>
                    </ChainOfThought>

                    <message-blocks
                        v-if="liveBlocks.length > 0"
                        :blocks="liveBlocks"
                    />
                    <span
                        v-if="liveText"
                        class="-mt-2 inline-block h-4 w-1.5 animate-pulse rounded-sm bg-gray-900 dark:bg-white"
                    ></span>
                </article>
            </ConversationContent>
            <ConversationScrollButton class="bottom-3" />
        </Conversation>

        <!-- Estado vazio: o agente como ponto de partida -->
        <div
            v-else
            class="flex min-h-0 flex-1 flex-col overflow-y-auto px-5 py-6"
        >
            <div class="my-auto flex w-full flex-col gap-6">
                <div class="flex flex-col gap-1.5">
                    <h2
                        class="text-xl font-semibold tracking-tight text-gray-900 dark:text-white"
                    >
                        {{
                            enabled
                                ? "O que você quer saber deste seller?"
                                : "Agente indisponível"
                        }}
                    </h2>
                    <p
                        class="text-sm leading-6 text-gray-500 dark:text-gray-400"
                    >
                        <template v-if="enabled">
                            Pergunte em linguagem natural. O agente cruza
                            alertas, velocity, transações, reembolsos e
                            identidades, e abre a evidência no painel ao lado.
                        </template>
                        <template v-else>
                            Parecer de IA desabilitado neste ambiente. Use os
                            comandos para navegar pelas visões.
                        </template>
                    </p>
                </div>

                <!-- Leitura rápida: os três julgamentos do caso -->
                <div class="flex flex-col gap-2">
                    <span
                        class="text-xs font-medium text-gray-400 dark:text-gray-500"
                    >
                        Leitura rápida
                    </span>
                    <div
                        class="flex flex-col divide-y divide-gray-100 overflow-hidden rounded-xl border border-gray-200 dark:divide-white/5 dark:border-white/10"
                    >
                        <template v-if="signalsLoading">
                            <div
                                v-for="n in 3"
                                :key="n"
                                class="flex items-center gap-3 px-3 py-2.5"
                            >
                                <span
                                    class="h-2 w-2 rounded-full bg-gray-200 dark:bg-white/10"
                                ></span>
                                <span
                                    class="h-3 w-40 animate-pulse rounded bg-gray-100 dark:bg-white/5"
                                ></span>
                            </div>
                        </template>
                        <button
                            v-else
                            v-for="signal in signals"
                            :key="signal.key"
                            type="button"
                            class="group/sig flex items-center gap-3 px-3 py-2.5 text-left transition-colors hover:bg-gray-50 focus:outline-none focus-visible:bg-gray-50 dark:hover:bg-white/5 dark:focus-visible:bg-white/5"
                            @click="$emit('navigate', signal.section)"
                        >
                            <span
                                class="h-2 w-2 shrink-0 rounded-full"
                                :class="signalDot(signal.status)"
                            ></span>
                            <span class="flex min-w-0 flex-1 flex-col">
                                <span
                                    class="text-[13px] font-medium text-gray-900 dark:text-white"
                                >
                                    {{ signal.question }}
                                </span>
                                <span
                                    class="truncate text-xs text-gray-500 dark:text-gray-400"
                                    :title="signal.reason"
                                >
                                    {{ signal.reason }}
                                </span>
                            </span>
                            <ChevronRightIcon
                                class="size-4 shrink-0 text-gray-300 transition-transform group-hover/sig:translate-x-0.5 dark:text-gray-600"
                                aria-hidden="true"
                            />
                        </button>
                    </div>
                </div>

                <div v-if="enabled" class="grid grid-cols-2 gap-2">
                    <button
                        v-for="starter in starters"
                        :key="starter.prompt"
                        type="button"
                        class="flex flex-col items-start gap-2 rounded-xl border border-gray-200 p-3 text-left transition-colors hover:border-gray-300 hover:bg-gray-50 focus:outline-none focus-visible:ring-2 focus-visible:ring-indigo-500 disabled:opacity-50 dark:border-white/10 dark:hover:border-white/20 dark:hover:bg-white/5"
                        :disabled="loadingLatest"
                        @click="sendMessage(starter.prompt)"
                    >
                        <component
                            :is="starter.icon"
                            class="size-4 text-gray-400 dark:text-gray-500"
                            aria-hidden="true"
                        />
                        <span class="flex flex-col gap-0.5">
                            <span
                                class="text-[13px] font-medium text-gray-900 dark:text-white"
                            >
                                {{ starter.prompt }}
                            </span>
                            <span
                                class="text-xs leading-5 text-gray-500 dark:text-gray-400"
                            >
                                {{ starter.hint }}
                            </span>
                        </span>
                    </button>
                </div>
            </div>
        </div>

        <!-- Composer -->
        <div class="shrink-0 px-4 pb-4">
            <div
                v-if="error"
                class="mb-2 rounded-lg bg-rose-50 px-3 py-2 text-xs text-rose-600 dark:bg-rose-500/10 dark:text-rose-400"
            >
                {{ error }}
            </div>

            <!-- Próximas perguntas -->
            <div
                v-if="
                    enabled &&
                    thread.length > 0 &&
                    !streaming &&
                    !menuOpen &&
                    !loadingLatest
                "
                class="no-scrollbar -mx-4 mb-2 overflow-x-auto px-4"
            >
                <div class="flex w-max gap-1.5">
                    <button
                        v-for="suggestion in followUps"
                        :key="suggestion"
                        type="button"
                        class="rounded-full border border-gray-200 px-3 py-1 text-xs text-gray-600 transition-colors hover:border-gray-300 hover:bg-gray-50 hover:text-gray-900 focus:outline-none focus-visible:ring-2 focus-visible:ring-indigo-500 dark:border-white/10 dark:text-gray-300 dark:hover:bg-white/5 dark:hover:text-white"
                        @click="sendMessage(suggestion)"
                    >
                        {{ suggestion }}
                    </button>
                </div>
            </div>

            <!-- Recibo de comando executado -->
            <transition
                enter-active-class="transition duration-150 ease-out"
                enter-from-class="translate-y-1 opacity-0"
                leave-active-class="transition duration-200 ease-in"
                leave-to-class="opacity-0"
            >
                <div
                    v-if="receipt"
                    class="mb-2 flex items-center gap-1.5 text-xs text-gray-500 dark:text-gray-400"
                    role="status"
                >
                    <CheckIcon
                        class="size-3.5 text-emerald-500"
                        aria-hidden="true"
                    />
                    {{ receipt }}
                </div>
            </transition>

            <div class="relative">
                <!-- Paleta de comandos ("/") -->
                <transition
                    enter-active-class="transition duration-100 ease-out"
                    enter-from-class="translate-y-1 opacity-0"
                    leave-active-class="transition duration-75 ease-in"
                    leave-to-class="opacity-0"
                >
                    <div
                        v-if="menuOpen"
                        class="absolute inset-x-0 bottom-full z-20 mb-2 max-h-[360px] overflow-y-auto rounded-xl border border-gray-200 bg-white p-1.5 shadow-[0_16px_40px_rgba(20,21,26,0.14)] dark:border-white/10 dark:bg-gray-800"
                        role="listbox"
                        aria-label="Comandos do agente"
                    >
                        <p
                            v-if="filteredCommands.length === 0"
                            class="px-2.5 py-2 text-xs text-gray-400"
                        >
                            Nenhum comando para “{{ commandQuery }}”.
                        </p>
                        <template
                            v-for="group in commandGroups"
                            :key="group.name"
                        >
                            <p
                                class="px-2.5 pb-1 pt-2 text-[11px] font-medium text-gray-400 first:pt-1 dark:text-gray-500"
                            >
                                {{ group.name }}
                            </p>
                            <button
                                v-for="command in group.items"
                                :key="command.id"
                                type="button"
                                role="option"
                                :aria-selected="
                                    filteredCommands[menuIndex]?.id ===
                                    command.id
                                "
                                class="flex w-full items-center gap-2.5 rounded-lg px-2.5 py-2 text-left text-[13px] text-gray-700 dark:text-gray-200"
                                :class="
                                    filteredCommands[menuIndex]?.id ===
                                    command.id
                                        ? 'bg-gray-100 dark:bg-white/10'
                                        : ''
                                "
                                @mouseenter="
                                    menuIndex =
                                        filteredCommands.indexOf(command)
                                "
                                @mousedown.prevent="runCommand(command)"
                            >
                                <component
                                    :is="command.icon"
                                    class="size-4 shrink-0 text-gray-400"
                                    aria-hidden="true"
                                />
                                <span class="flex-1 truncate">
                                    {{ command.label }}
                                </span>
                                <span
                                    v-if="command.meta"
                                    class="shrink-0 text-xs text-gray-400 dark:text-gray-500"
                                >
                                    {{ command.meta }}
                                </span>
                                <span
                                    v-if="command.dot"
                                    class="h-1.5 w-1.5 shrink-0 rounded-full"
                                    :class="command.dot"
                                ></span>
                            </button>
                        </template>
                    </div>
                </transition>

                <form
                    class="flex flex-col rounded-2xl border border-gray-200 bg-white shadow-[0_2px_12px_rgba(20,21,26,0.06)] transition-colors focus-within:border-gray-300 dark:border-white/10 dark:bg-gray-800/60 dark:focus-within:border-white/20"
                    @submit.prevent="submit"
                >
                    <textarea
                        ref="input"
                        v-model="draft"
                        rows="1"
                        class="max-h-[180px] w-full resize-none border-0 bg-transparent px-4 pb-1 pt-3.5 text-sm leading-6 text-gray-900 placeholder:text-gray-400 focus:outline-none focus:ring-0 dark:text-white dark:placeholder:text-gray-500"
                        :placeholder="
                            enabled
                                ? 'Pergunte ou digite / para comandos'
                                : 'Digite / para comandos'
                        "
                        maxlength="2000"
                        aria-label="Mensagem para o agente"
                        @input="autosize"
                        @keydown="onKeydown"
                        @focus="menuOpen = draft.startsWith('/')"
                        @blur="menuOpen = false"
                    ></textarea>
                    <div class="flex items-center gap-1 px-2 pb-2">
                        <button
                            type="button"
                            class="flex h-8 w-8 items-center justify-center rounded-full text-gray-500 transition-colors hover:bg-gray-100 hover:text-gray-900 focus:outline-none focus-visible:ring-2 focus-visible:ring-indigo-500 dark:text-gray-400 dark:hover:bg-white/10 dark:hover:text-white"
                            aria-label="Abrir comandos"
                            v-tooltip="'Comandos'"
                            @mousedown.prevent="openMenu"
                        >
                            <PlusIcon class="size-4" aria-hidden="true" />
                        </button>
                        <button
                            type="button"
                            class="flex h-8 items-center gap-1.5 rounded-full px-2.5 text-xs text-gray-500 transition-colors hover:bg-gray-100 hover:text-gray-900 focus:outline-none focus-visible:ring-2 focus-visible:ring-indigo-500 dark:text-gray-400 dark:hover:bg-white/10 dark:hover:text-white"
                            v-tooltip="'Período analisado'"
                            @mousedown.prevent="openMenu('período')"
                        >
                            <CalendarRangeIcon
                                class="size-3.5"
                                aria-hidden="true"
                            />
                            {{ periodLabel(period) }}
                        </button>
                        <span class="flex-1"></span>
                        <button
                            type="submit"
                            class="flex h-8 w-8 items-center justify-center rounded-full bg-gray-900 text-white transition-colors hover:bg-gray-700 focus:outline-none focus-visible:ring-2 focus-visible:ring-indigo-500 focus-visible:ring-offset-2 disabled:bg-gray-200 disabled:text-gray-400 dark:bg-white dark:text-gray-900 dark:hover:bg-gray-200 dark:focus-visible:ring-offset-gray-900 dark:disabled:bg-white/10 dark:disabled:text-gray-500"
                            :disabled="!canSend"
                            :aria-label="streaming ? 'Investigando' : 'Enviar'"
                        >
                            <LoaderCircleIcon
                                v-if="streaming"
                                class="size-4 animate-spin"
                                aria-hidden="true"
                            />
                            <ArrowUpIcon
                                v-else
                                class="size-4"
                                aria-hidden="true"
                            />
                        </button>
                    </div>
                </form>
            </div>
            <p
                class="mt-2 text-center text-[11px] text-gray-400 dark:text-gray-500"
            >
                O agente consulta dados de produção. Confira a evidência antes
                de decidir.
            </p>
        </div>
    </section>
</template>

<script>
import { defineComponent, h } from "vue";
import {
    ActivityIcon,
    ArrowUpIcon,
    ArrowUpRightIcon,
    CalendarRangeIcon,
    CheckCircle2Icon,
    CheckIcon,
    ChevronRightIcon,
    CircleHelpIcon,
    CopyIcon,
    FileTextIcon,
    GavelIcon,
    HistoryIcon,
    LayoutDashboardIcon,
    LoaderCircleIcon,
    PlusIcon,
    ScaleIcon,
    ScanSearchIcon,
    ShieldAlertIcon,
    SparklesIcon,
    SquarePenIcon,
} from "@lucide/vue";
import {
    ChainOfThought,
    ChainOfThoughtContent,
    ChainOfThoughtHeader,
    ChainOfThoughtStep,
} from "@/components/ai-elements/chain-of-thought";
import {
    Conversation,
    ConversationContent,
    ConversationScrollButton,
} from "@/components/ai-elements/conversation";
import { Loader } from "@/components/ai-elements/loader";
import { Shimmer } from "@/components/ai-elements/shimmer";
import HighlightedText from "../../../views/monitoring/HighlightedText.vue";
import { getEcho } from "@/lib/echo";

const TOOL_LABELS = {
    ProducerMonitoringSignalsTool:
        "Consultando alertas e incidentes do monitoramento",
    ProducerProfileTool: "Levantando perfil e histórico da conta",
    ProducerCardVelocityTool: "Analisando velocity de recusas por BIN",
    ProducerTransactionBreakdownTool: "Abrindo o breakdown de transações",
    ProducerRefundSpeedTool: "Medindo velocidade de reembolso",
    ProducerIdentityStatsTool: "Cruzando identidades e fingerprints",
    ProducerDailyTimelineTool: "Reconstruindo a timeline diária",
    ProducerScoreBreakdownTool: "Abrindo o score e os fatores do cálculo",
    ProducerReserveRunoffTool: "Cruzando reserva, runoff e checkpoints",
};

/* Mesmos chips do vocabulário visual do monitoring (labels.js). */
const VERDICTS = {
    SIM: {
        label: "Fraude: SIM",
        class: "bg-rose-500/10 text-rose-600 ring-1 ring-inset ring-rose-500/25 dark:text-rose-400",
    },
    SUSPEITO: {
        label: "Suspeito",
        class: "bg-amber-400/10 text-amber-600 ring-1 ring-inset ring-amber-400/30 dark:text-amber-400",
    },
    NAO: {
        label: "Fraude: NÃO",
        class: "bg-emerald-500/10 text-emerald-600 ring-1 ring-inset ring-emerald-500/25 dark:text-emerald-400",
    },
};

/* Ícone de cada visão do painel (mesmas chaves do AuditUserMetrics). */
const VIEW_ICONS = {
    overview: LayoutDashboardIcon,
    runoff: ScaleIcon,
    conduta: ShieldAlertIcon,
    operacao: ActivityIcon,
    hist: HistoryIcon,
    retrato: ScanSearchIcon,
};

/* Alvos de navegação que não são uma visão (vivem dentro de uma). */
const TARGET_VIEW = { score: "conduta" };

const STATUS_DOTS = {
    danger: "bg-rose-500",
    critical: "bg-rose-500",
    warning: "bg-amber-500",
    attention: "bg-amber-500",
};

/**
 * Parser leve do markdown do agente (headings ## e bullets) para renderizar
 * cada linha com o HighlightedText do monitoring, em vez de HTML via
 * markdown-it.
 */
function parseBlocks(text) {
    const body = (text ?? "")
        .replace(/^\s*VEREDITO:.*$/im, "")
        .replace(/^\s*CONFIANCA:.*$/im, "")
        .replace(/^\s*CONFIANÇA:.*$/im, "")
        .trim();

    const blocks = [];

    for (const rawLine of body.split("\n")) {
        const line = rawLine
            .replace(/\*\*(.+?)\*\*/g, "$1")
            .replace(/__(.+?)__/g, "$1")
            .trim();

        if (line === "") {
            continue;
        }

        const heading = line.match(/^#{1,4}\s+(.*)$/);

        if (heading) {
            blocks.push({ type: "heading", text: heading[1] });
            continue;
        }

        const bullet = line.match(/^[-*]\s+(.*)$/);

        if (bullet) {
            const last = blocks[blocks.length - 1];

            if (last?.type === "list") {
                last.items.push(bullet[1]);
            } else {
                blocks.push({ type: "list", items: [bullet[1]] });
            }

            continue;
        }

        blocks.push({ type: "paragraph", text: line });
    }

    return blocks;
}

/* Cards do retrato que o chat mostra como evidência de cada visão. */
const EVIDENCE_KEYS = {
    overview: ["coverage", "cashouts", "disputes", "tpv"],
    runoff: ["coverage", "exposure", "reserve", "cashouts"],
    conduta: ["disputes", "box_disputes", "refunds", "card_share"],
    score: ["disputes", "box_disputes", "refunds", "card_share"],
    operacao: ["tpv", "sales_count", "days_with_sale"],
    retrato: ["coverage", "exposure", "disputes", "tpv"],
};

/* Render dos blocos de uma mensagem do agente com o tokenizer do monitoring. */
const MessageBlocks = defineComponent({
    name: "MessageBlocks",
    components: { HighlightedText },
    props: {
        blocks: { type: Array, required: true },
    },
    render() {
        return h(
            "div",
            { class: "flex flex-col gap-2.5" },
            this.blocks.map((block) => {
                if (block.type === "heading") {
                    return h(
                        "h4",
                        {
                            class: "mt-2 text-[13px] font-semibold text-gray-900 first:mt-0 dark:text-white",
                        },
                        block.text,
                    );
                }

                if (block.type === "list") {
                    return h(
                        "ul",
                        { class: "flex flex-col gap-1.5" },
                        block.items.map((item) =>
                            h(
                                "li",
                                {
                                    class: "flex gap-2.5 text-sm leading-6 text-gray-700 dark:text-gray-300",
                                },
                                [
                                    h("span", {
                                        class: "mt-[11px] h-1 w-1 shrink-0 rounded-full bg-gray-400 dark:bg-gray-500",
                                    }),
                                    h("span", { class: "min-w-0" }, [
                                        h(HighlightedText, { text: item }),
                                    ]),
                                ],
                            ),
                        ),
                    );
                }

                return h(
                    "p",
                    {
                        class: "text-sm leading-6 text-gray-700 dark:text-gray-300",
                    },
                    [h(HighlightedText, { text: block.text })],
                );
            }),
        );
    },
});

export default {
    name: "SellerAiAudit",

    components: {
        ChainOfThought,
        ChainOfThoughtContent,
        ChainOfThoughtHeader,
        ChainOfThoughtStep,
        Conversation,
        ConversationContent,
        ConversationScrollButton,
        Loader,
        Shimmer,
        MessageBlocks,
        ArrowUpIcon,
        ArrowUpRightIcon,
        CalendarRangeIcon,
        CheckCircle2Icon,
        CheckIcon,
        ChevronRightIcon,
        CircleHelpIcon,
        CopyIcon,
        LoaderCircleIcon,
        PlusIcon,
        SparklesIcon,
        SquarePenIcon,
    },

    props: {
        user: {
            type: Object,
            required: true,
        },
        // Cards do retrato (mesmos do painel): viram evidência no chat
        cards: {
            type: Array,
            default: () => [],
        },
        // Visões do painel: [{ key, label, priority, question }]
        views: {
            type: Array,
            default: () => [],
        },
        activeView: {
            type: String,
            default: null,
        },
        period: {
            type: Number,
            default: 30,
        },
        periodOptions: {
            type: Array,
            default: () => [7, 15, 30, 90, 0],
        },
        // Julgamentos do caso (SellerFarol) para a leitura rápida
        signals: {
            type: Array,
            default: () => [],
        },
        signalsLoading: {
            type: Boolean,
            default: false,
        },
        hasOpenCase: {
            type: Boolean,
            default: false,
        },
    },

    emits: ["verdict", "tour", "navigate", "period", "dossier", "decide"],

    data() {
        return {
            enabled: true,
            loadingLatest: true,
            streaming: false,
            messages: [],
            review: null,
            verdict: null,
            confidence: null,
            createdAt: null,
            fresh: false,
            liveSteps: [],
            liveText: "",
            error: null,
            draft: "",
            pollTimer: null,
            pollStartedAt: null,
            realtime: false,
            finishing: false,
            // Alvo de navegação da pergunta em andamento
            pendingNav: null,
            // Paleta de comandos
            menuOpen: false,
            menuIndex: 0,
            receipt: "",
            receiptTimer: null,
            copiedIndex: null,
            starters: [
                {
                    prompt: "Gerar parecer completo",
                    hint: "Veredito com alertas, velocity e reembolsos",
                    icon: SparklesIcon,
                },
                {
                    prompt: "Posso liberar o saque?",
                    hint: "Reserva, runoff e cobertura",
                    icon: ScaleIcon,
                },
                {
                    prompt: "Tem sinal de fraude?",
                    hint: "Disputas, score e BINs",
                    icon: ShieldAlertIcon,
                },
                {
                    prompt: "O negócio está vivo?",
                    hint: "Ritmo de vendas e métodos",
                    icon: ActivityIcon,
                },
            ],
        };
    },

    computed: {
        verdictLabel() {
            return VERDICTS[this.verdict]?.label ?? this.verdict;
        },

        verdictBadgeClass() {
            return (
                VERDICTS[this.verdict]?.class ??
                "bg-gray-100 text-gray-600 dark:bg-gray-700 dark:text-gray-300"
            );
        },

        verdictTooltip() {
            if (!this.createdAt) {
                return "Último parecer";
            }

            const label = new Date(this.createdAt).toLocaleString("pt-BR", {
                day: "2-digit",
                month: "2-digit",
                hour: "2-digit",
                minute: "2-digit",
            });

            return this.fresh
                ? `Parecer de ${label}`
                : `Parecer de ${label} · desatualizado`;
        },

        /**
         * Conversa a renderizar. Sem mensagens, o último parecer persistido
         * vira o primeiro turno do agente para dar contexto ao chat.
         */
        thread() {
            if (this.messages.length > 0) {
                return this.messages;
            }

            if (this.review?.report) {
                return [
                    {
                        role: "assistant",
                        text: this.review.report,
                        blocks: parseBlocks(this.review.report),
                        steps: (this.review.tool_steps ?? []).map(
                            (step, index) => ({
                                id: `saved-${index}`,
                                label: TOOL_LABELS[step.tool] ?? step.tool,
                                detail: stepDetail(step.arguments),
                                done: true,
                            }),
                        ),
                        stepsOpen: false,
                    },
                ];
            }

            return [];
        },

        liveBlocks() {
            return parseBlocks(this.liveText);
        },

        followUps() {
            const items = [];

            if (!this.verdict || !this.fresh) {
                items.push("Gerar parecer completo");
            }

            items.push(
                "Posso liberar o saque?",
                "Por que o score caiu?",
                "Resuma o histórico de decisões",
            );

            return items;
        },

        canSend() {
            return (
                this.enabled &&
                !this.streaming &&
                this.draft.trim() !== "" &&
                !this.draft.trim().startsWith("/")
            );
        },

        commandQuery() {
            return this.draft.startsWith("/")
                ? this.draft.slice(1).trim().toLowerCase()
                : "";
        },

        commands() {
            const views = this.views.map((view) => ({
                id: `view:${view.key}`,
                group: "Visualizar",
                label: view.label,
                meta: this.activeView === view.key ? "aberto" : "",
                dot: STATUS_DOTS[view.priority] ?? null,
                icon: VIEW_ICONS[view.key] ?? LayoutDashboardIcon,
                keywords: `abrir ver ${view.label}`,
                run: () => {
                    this.$emit("navigate", view.key);
                    this.flashReceipt(`${view.label} aberto no painel`);
                },
            }));

            const periods = this.periodOptions.map((option) => ({
                id: `period:${option}`,
                group: "Período",
                label: this.periodLabel(option, true),
                meta: this.period === option ? "atual" : "",
                icon: CalendarRangeIcon,
                keywords: `período periodo janela dias ${option}`,
                run: () => {
                    this.$emit("period", option);
                    this.flashReceipt(
                        `Período alterado para ${this.periodLabel(option, true).toLowerCase()}`,
                    );
                },
            }));

            const actions = [];

            if (this.enabled) {
                actions.push({
                    id: "action:review",
                    group: "Ações",
                    label: "Gerar parecer completo",
                    icon: SparklesIcon,
                    keywords: "parecer análise analise veredito",
                    run: () => this.sendMessage("Gerar parecer completo"),
                });
            }

            if (this.hasOpenCase) {
                actions.push({
                    id: "action:decide",
                    group: "Ações",
                    label: "Decidir o caso aberto",
                    icon: GavelIcon,
                    keywords: "decidir decisão liberar travar estender",
                    run: () => {
                        this.$emit("decide");
                        this.flashReceipt("Barra de decisão em destaque");
                    },
                });
            }

            actions.push(
                {
                    id: "action:dossier",
                    group: "Ações",
                    label: "Gerar dossiê",
                    icon: FileTextIcon,
                    keywords: "dossiê dossie pdf relatório",
                    run: () => {
                        this.$emit("dossier");
                        this.flashReceipt("Gerando dossiê em nova aba");
                    },
                },
                {
                    id: "action:tour",
                    group: "Ações",
                    label: "Tour da tela",
                    icon: CircleHelpIcon,
                    keywords: "tour ajuda",
                    run: () => this.$emit("tour"),
                },
            );

            if (this.messages.length > 0) {
                actions.push({
                    id: "action:clear",
                    group: "Ações",
                    label: "Nova conversa",
                    icon: SquarePenIcon,
                    keywords: "limpar nova conversa reset",
                    run: () => this.clearConversation(),
                });
            }

            return [...views, ...periods, ...actions];
        },

        filteredCommands() {
            const query = this.normalize(this.commandQuery);

            if (query === "") {
                return this.commands;
            }

            return this.commands.filter((command) =>
                this.normalize(
                    `${command.group} ${command.label} ${command.keywords ?? ""}`,
                ).includes(query),
            );
        },

        commandGroups() {
            const groups = [];

            for (const command of this.filteredCommands) {
                let group = groups.find((item) => item.name === command.group);

                if (!group) {
                    group = { name: command.group, items: [] };
                    groups.push(group);
                }

                group.items.push(command);
            }

            return groups;
        },
    },

    watch: {
        draft(value) {
            this.menuOpen = value.startsWith("/");
        },

        commandQuery() {
            this.menuIndex = 0;
        },
    },

    mounted() {
        this.fetchConversation();
        this.subscribe();
    },

    beforeUnmount() {
        this.stopPolling();
        this.unsubscribe();
        clearTimeout(this.receiptTimer);
    },

    methods: {
        userId() {
            return this.user?.id ?? this.$route.params.user_id;
        },

        normalize(text) {
            return text.toLowerCase().normalize("NFD").replace(/[̀-ͯ]/g, "");
        },

        periodLabel(option, long = false) {
            if (option === 0) {
                return long ? "Todo o histórico" : "Total";
            }

            return long ? `Últimos ${option} dias` : `${option}d`;
        },

        viewOf(target) {
            return TARGET_VIEW[target] ?? target;
        },

        viewIcon(target) {
            return VIEW_ICONS[this.viewOf(target)] ?? LayoutDashboardIcon;
        },

        viewLabel(target) {
            if (target === "score") {
                return "Score";
            }

            return (
                this.views.find((view) => view.key === this.viewOf(target))
                    ?.label ?? target
            );
        },

        signalDot(status) {
            return STATUS_DOTS[status] ?? "bg-emerald-500";
        },

        /** Cards do retrato que sustentam a resposta, por visão. */
        evidenceFor(target) {
            const byKey = Object.fromEntries(
                this.cards.map((card) => [card.key, card]),
            );

            return (EVIDENCE_KEYS[target] ?? [])
                .map((key) => byKey[key])
                .filter(Boolean);
        },

        evidenceDot(status) {
            return (
                {
                    critical: "bg-rose-500",
                    attention: "bg-amber-500",
                }[status] ?? "bg-gray-300 dark:bg-gray-600"
            );
        },

        sparkPath(series, area = false) {
            const values = series.map((point) => Number(point.value) || 0);
            const max = Math.max(...values);
            const min = Math.min(...values);
            const range = max - min || 1;
            const step = 100 / (values.length - 1);
            const line = values
                .map(
                    (value, index) =>
                        `${index ? "L" : "M"}${(index * step).toFixed(2)},${(22 - ((value - min) / range) * 18).toFixed(2)}`,
                )
                .join(" ");

            return area ? `${line} L100,24 L0,24 Z` : line;
        },

        /**
         * Visão que sustenta a resposta do agente: a intenção da pergunta
         * que a originou. O parecer salvo (sem pergunta) aponta para a
         * visão geral.
         */
        navFor(index) {
            const message = this.thread[index];

            if (message?.role !== "assistant") {
                return null;
            }

            const question = this.thread
                .slice(0, index)
                .reverse()
                .find((item) => item.role === "user");

            if (!question) {
                return { target: "overview" };
            }

            return this.intentNav(question.text);
        },

        mapMessages(raw) {
            return (raw ?? []).map((message, index) => ({
                role: message.role,
                text: message.text,
                blocks:
                    message.role === "assistant"
                        ? parseBlocks(message.text)
                        : [],
                steps: (message.tool_steps ?? []).map((step, stepIndex) => ({
                    id: `s-${index}-${stepIndex}`,
                    label: TOOL_LABELS[step.tool] ?? step.tool,
                    detail: stepDetail(step.arguments),
                    done: true,
                })),
                stepsOpen: false,
            }));
        },

        async fetchConversation() {
            this.loadingLatest = true;

            try {
                const [latest, chat] = await Promise.all([
                    axios.get(`/api/user/${this.userId()}/audit/ai-review`),
                    axios.get(
                        `/api/user/${this.userId()}/audit/ai-review/chat`,
                    ),
                ]);

                this.enabled = latest.data.enabled;
                this.review = latest.data.review;

                if (latest.data.review) {
                    this.verdict = latest.data.review.verdict;
                    this.confidence = latest.data.review.confidence;
                    this.createdAt = latest.data.review.created_at;
                    this.fresh = latest.data.review.fresh;
                    this.emitVerdict();
                }

                this.messages = this.mapMessages(chat.data.messages);

                if (chat.data.running) {
                    this.streaming = true;
                    this.startPolling();
                }
            } catch (error) {
                // Sem conversa ou parecer não é erro: o painel oferece o input.
            } finally {
                this.loadingLatest = false;
            }
        },

        submit() {
            if (this.menuOpen) {
                this.runHighlighted();
                return;
            }

            const message = this.draft.trim();

            if (this.canSend && message !== "") {
                this.sendMessage(message);
            }
        },

        onKeydown(event) {
            if (this.menuOpen) {
                const total = this.filteredCommands.length;

                if (event.key === "ArrowDown" && total > 0) {
                    event.preventDefault();
                    this.menuIndex = (this.menuIndex + 1) % total;
                    this.scrollMenuItem();
                    return;
                }

                if (event.key === "ArrowUp" && total > 0) {
                    event.preventDefault();
                    this.menuIndex = (this.menuIndex - 1 + total) % total;
                    this.scrollMenuItem();
                    return;
                }

                if (event.key === "Escape") {
                    event.preventDefault();
                    this.draft = "";
                    return;
                }

                if (event.key === "Enter" || event.key === "Tab") {
                    event.preventDefault();
                    this.runHighlighted();
                    return;
                }
            }

            if (
                event.key === "Enter" &&
                !event.shiftKey &&
                !event.isComposing
            ) {
                event.preventDefault();
                this.submit();
            }
        },

        scrollMenuItem() {
            this.$nextTick(() => {
                this.$el
                    .querySelector('[role="option"][aria-selected="true"]')
                    ?.scrollIntoView({ block: "nearest" });
            });
        },

        openMenu(query = "") {
            this.draft = typeof query === "string" && query ? `/${query}` : "/";
            this.menuOpen = true;
            this.$nextTick(() => {
                this.$refs.input?.focus();
                this.autosize();
            });
        },

        runHighlighted() {
            const command = this.filteredCommands[this.menuIndex];

            if (command) {
                this.runCommand(command);
            }
        },

        runCommand(command) {
            this.draft = "";
            this.menuOpen = false;
            this.$nextTick(() => this.autosize());
            command.run();
        },

        flashReceipt(text) {
            clearTimeout(this.receiptTimer);
            this.receipt = text;
            this.receiptTimer = setTimeout(() => {
                this.receipt = "";
            }, 2600);
        },

        autosize() {
            const input = this.$refs.input;

            if (!input) {
                return;
            }

            input.style.height = "auto";
            input.style.height = `${Math.min(input.scrollHeight, 180)}px`;
        },

        async copy(text, index) {
            try {
                await navigator.clipboard.writeText(text ?? "");
                this.copiedIndex = index;
                setTimeout(() => {
                    if (this.copiedIndex === index) {
                        this.copiedIndex = null;
                    }
                }, 1600);
            } catch (error) {
                // Sem clipboard (contexto inseguro): o botão só não confirma.
            }
        },

        /**
         * Roteia a pergunta para a visão que sustenta a resposta (regex de
         * intenção). Devolve null quando a pergunta não tem um "lugar" no
         * painel.
         */
        intentNav(text) {
            const normalized = this.normalize(text ?? "");

            if (/histor|decis|checkpoint/.test(normalized)) {
                return { target: "hist" };
            }

            if (/fraude|disput|chargeback|teste de cart|bin/.test(normalized)) {
                return { target: "conduta" };
            }

            if (/score|fator/.test(normalized)) {
                return { target: "score" };
            }

            if (/liber|saque|sacar|runoff|reserva|cobertura/.test(normalized)) {
                return { target: "runoff" };
            }

            if (
                /vend|vivo|ritmo|tpv|comiss|metodo|faturamento/.test(normalized)
            ) {
                return { target: "operacao" };
            }

            if (/retrato|ficha|regua|evidencia/.test(normalized)) {
                return { target: "retrato" };
            }

            if (/anal|risco|parecer|completo/.test(normalized)) {
                return { target: "overview" };
            }

            return null;
        },

        async sendMessage(text) {
            if (!this.enabled || this.streaming) {
                return;
            }

            this.error = null;
            this.draft = "";
            this.$nextTick(() => this.autosize());

            // Abre a visão alvo já no início da investigação; repete ao
            // concluir (applyLiveSnapshot) para reaplicar o destaque.
            this.pendingNav = this.intentNav(text);

            if (this.pendingNav) {
                this.$emit("navigate", this.pendingNav.target);
            }

            // O parecer salvo vira mensagem real ao iniciar a conversa.
            if (this.messages.length === 0 && this.thread.length > 0) {
                this.messages = this.thread;
            }

            this.messages = [
                ...this.messages,
                { role: "user", text, blocks: [], steps: [], stepsOpen: false },
            ];

            this.streaming = true;
            this.liveSteps = [];
            this.liveText = "";

            try {
                await axios.post(
                    `/api/user/${this.userId()}/audit/ai-review/chat`,
                    { message: text },
                );

                this.startPolling();
            } catch (error) {
                this.streaming = false;
                this.error =
                    error.response?.data?.message ??
                    "Falha ao enviar a mensagem.";
            }
        },

        async clearConversation() {
            try {
                await axios.delete(
                    `/api/user/${this.userId()}/audit/ai-review/chat`,
                );
                this.messages = [];
                this.flashReceipt("Nova conversa iniciada");
            } catch (error) {
                this.error = "Falha ao limpar a conversa.";
            }
        },

        /**
         * Stream em tempo real via Pusher: cada flush do job chega como
         * evento .progress com o mesmo shape do snapshot do polling. O
         * polling continua rodando como fallback, mais lento quando o
         * canal está de pé.
         */
        subscribe() {
            const echo = getEcho();

            if (echo === null) {
                return;
            }

            this.channel = echo
                .private(`seller-ai-review.${this.userId()}`)
                .listen(".progress", (snapshot) => {
                    this.realtime = true;

                    if (this.streaming) {
                        this.applyLiveSnapshot(snapshot);
                    }
                });
        },

        unsubscribe() {
            if (this.channel) {
                getEcho()?.leave(`seller-ai-review.${this.userId()}`);
                this.channel = null;
            }
        },

        async applyLiveSnapshot(snapshot) {
            if (snapshot.status === "done") {
                if (this.finishing) {
                    return;
                }

                // Busca a conversa final ANTES de derrubar o turno de
                // streaming: derrubar primeiro faz a resposta sumir e
                // reaparecer (flicker).
                this.finishing = true;
                this.stopPolling();

                try {
                    await this.fetchConversation();
                } finally {
                    this.streaming = false;
                    this.liveSteps = [];
                    this.liveText = "";
                    this.finishing = false;

                    if (this.pendingNav) {
                        this.$emit("navigate", this.pendingNav.target);
                        this.pendingNav = null;
                    }
                }

                return;
            }

            this.liveText = snapshot.text ?? "";
            this.liveSteps = (snapshot.steps ?? []).map((step) => ({
                id: step.id,
                label: TOOL_LABELS[step.tool] ?? step.tool,
                detail: stepDetail(step.arguments),
                done: step.done,
            }));

            if (snapshot.status === "failed") {
                this.stopPolling();
                this.streaming = false;
                this.liveSteps = [];
                this.liveText = "";
                this.error = snapshot.error ?? "Falha ao responder.";
            }
        },

        startPolling() {
            this.pollStartedAt = Date.now();
            this.stopPolling();
            this.pollTimer = setInterval(
                () => this.poll(),
                this.realtime ? 10000 : 2500,
            );
            this.poll();
        },

        stopPolling() {
            if (this.pollTimer) {
                clearInterval(this.pollTimer);
                this.pollTimer = null;
            }
        },

        async poll() {
            if (Date.now() - this.pollStartedAt > 6 * 60 * 1000) {
                this.stopPolling();
                this.streaming = false;
                this.error = "A análise demorou demais. Tente novamente.";
                return;
            }

            try {
                const { data } = await axios.get(
                    `/api/user/${this.userId()}/audit/ai-review/progress`,
                );

                if (data.status === "idle") {
                    return;
                }

                await this.applyLiveSnapshot(data);
            } catch (error) {
                // Erro transitório de rede: o próximo tick tenta de novo.
            }
        },

        emitVerdict() {
            this.$emit("verdict", {
                verdict: this.verdict,
                confidence: this.confidence,
                fresh: this.fresh,
            });
        },
    },
};

function stepDetail(args) {
    if (!args || typeof args !== "object") {
        return null;
    }

    if (args.bin) {
        return `BIN ${args.bin}`;
    }

    if (args.window_days) {
        return `${args.window_days}d`;
    }

    return null;
}
</script>
